/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { HallowAnalyticsEvent } from "@packages/analytics";
import {
  useRequestCommunitiesAdminCommunity,
  useRequestMe,
} from "@packages/sdk";
import {
  CommunitySelector,
  Divider,
  ExitIcon,
  ReportedCounter,
  SidebarTab,
  spacing,
} from "@packages/ui";
import { useParams } from "next/navigation";

import { getBasePADUrl, PAD_TABS } from "../../lib";
import { GivingStripeConnectSidebar } from "../parishAdminDashboard/giving/GivingStripeConnectButton/GivingStripeConnectSidebar";
import { CommunityInviteSidebarTab } from "../parishAdminDashboard/invite/CommunityInviteSidebarTab";

const menuBarLinksContainerStyles: Interpolation = {
  display: "flex",
  flexDirection: "column",
  flexGrow: 1,
  gap: spacing.ms,
};

// `margin-top: auto` pins the Invite entry (and its divider) to the bottom of
// the nav. The divider is a subtle separator between the main nav tabs and the
// pinned Invite item.
const inviteContainerStyles: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.ms,
  marginTop: "auto",
};

export type MenuBarDashboardMobileChildrenProps = {
  /**
   * Closes the full-screen mobile menu overlay. Passed to the Invite entry so
   * the menu is dismissed before the share modal opens — the menu renders above
   * the modal in the stacking order, so without this the modal would be hidden
   * behind it.
   */
  onCloseMenu?: () => void;
};

export const MenuBarDashboardMobileChildren = ({
  onCloseMenu,
}: MenuBarDashboardMobileChildrenProps) => {
  const params = useParams<{ communityId: string }>();
  const { query: communityQuery } = useRequestCommunitiesAdminCommunity({
    id: parseInt(params.communityId),
  });
  const { query: meQuery } = useRequestMe();

  const parishAdminBaseUrl = getBasePADUrl({
    communityId: parseInt(params.communityId),
  });

  return (
    <>
      <CommunitySelector triggerState="expanded" />
      <div css={menuBarLinksContainerStyles}>
        {PAD_TABS.map(
          ({
            sidebarState,
            path,
            Icon,
            getHidden,
            subTabs,
            message,
            getMessage,
            ...props
          }) => (
            <SidebarTab
              key={path}
              href={`${parishAdminBaseUrl}/${path}`}
              state={"expanded"}
              message={message ?? getMessage(communityQuery.data)}
              analyticsEvent={{
                event: HallowAnalyticsEvent.TappedAdminDashboardTab,
                properties: {
                  tab: path,
                },
              }}
              actionRight={
                (path === "reported" && (
                  <ReportedCounter communityId={parseInt(params.communityId)} />
                )) ||
                (path === "giving" && (
                  <GivingStripeConnectSidebar
                    communityId={parseInt(params.communityId)}
                  />
                ))
              }
              icon={<Icon fill="currentColor" />}
              hidden={
                getHidden
                  ? communityQuery.data && meQuery.data
                    ? getHidden({
                        community: communityQuery.data,
                        user: meQuery.data,
                      })
                    : true
                  : false
              }
              subTabs={subTabs?.map((subTab) => ({
                analyticsEvent:
                  path === "settings"
                    ? {
                        event: HallowAnalyticsEvent.TappedCommunitySettings,
                        properties: {
                          option: subTab.path.replace("-", "_"),
                        },
                      }
                    : {
                        event: HallowAnalyticsEvent.TappedAdminDashboardTab,
                        properties: {
                          tab: subTab.path,
                        },
                      },
                href: `${parishAdminBaseUrl}/${path}/${subTab.path}`,
                icon: <subTab.Icon fill="currentColor" />,
                getHidden: subTab.getHidden
                  ? communityQuery.data && meQuery.data
                    ? getHidden({
                        community: communityQuery.data,
                        user: meQuery.data,
                      })
                    : true
                  : false,
                message: subTab.message,
              }))}
              {...props}
            />
          ),
        )}
        <div css={inviteContainerStyles}>
          <Divider />
          <CommunityInviteSidebarTab
            community={communityQuery.data}
            state="expanded"
            onBeforeShow={onCloseMenu}
          />
        </div>
      </div>
      <SidebarTab
        href="/home"
        icon={<ExitIcon />}
        message="web_exit_dashboard"
        state="expanded"
      />
    </>
  );
};
